import { formatWholeNumber, wholeUnits } from "./format_number"

// GiveWell cost-per-output / cost-per-death-averted impact estimate for a flat donation amount
// split across weighted allocations -- OFTW's own methodology (distinct from HLI's WELLBY one in
// wellby_impact_calculator.js), ported from https://github.com/One-for-the-World/oftw_website's
// lib/impactCalculator.ts. Reusable by any client using this methodology, not a generic
// "impact calculator" for every client.

export function calculateOftwImpact({ amountCents, allocations, isZeroDecimal = false, unitsPerUsd = 1 }) {
  // Stripe zero-decimal currencies (JPY, KRW, VND, ...) have amountCents already in the currency's base unit
  const amount = isZeroDecimal ? amountCents : amountCents / 100

  // cost_per_output/cost_per_death_averted below are USD-denominated; unitsPerUsd (e.g. $1 USD = £0.79 GBP)
  // converts the donor's own-currency amount to its USD equivalent first, matching wellby_impact_calculator.js.
  const amountUsd = amount / unitsPerUsd

  // allocations' share_percent values don't necessarily sum to 100 -- a portfolio can mix charities
  // these figures cover with organizations that have no published cost-effectiveness data (e.g.
  // "Entire OFTW portfolio"'s discretionary-fund slice). Blended cost-per-death is reported against
  // the amount that actually produced averted deaths, not the donor's full donation.
  let totalDeathsAverted = 0
  let totalAllocatedUsd = 0
  const breakdown = allocations.map((allocation) => {
    const allocatedUsd = amountUsd * (allocation.share_percent / 100)
    const allocatedLocal = amount * (allocation.share_percent / 100)
    const units = allocation.cost_per_output > 0 ? allocatedUsd / allocation.cost_per_output : 0
    const deathsAverted = allocation.cost_per_death_averted > 0 ? allocatedUsd / allocation.cost_per_death_averted : 0
    totalDeathsAverted += deathsAverted
    totalAllocatedUsd += allocatedUsd

    return {
      name: allocation.name,
      logo: allocation.logo,
      // formatUnits drops decimals, so it's "1" whenever the displayed count is one.
      outcomeLabel: (wholeUnits(units) === 1 && allocation.outcome_label_singular) || allocation.outcome_label,
      costPerOutput: allocation.cost_per_output,
      sharePercent: allocation.share_percent,
      units,
      deathsAverted,
      allocatedLocal
    }
  })

  const costPerDeathAverted = totalDeathsAverted > 0 ? totalAllocatedUsd / totalDeathsAverted : 0
  const matchedSharePercent = allocations.reduce((sum, a) => sum + a.share_percent, 0)

  return { totalDeathsAverted, costPerDeathAverted, matchedSharePercent, breakdown }
}

// Program outputs are whole things in copy: "7.05 bednets" -> "7", "1204.7" -> "1,204".
export function formatUnits(units) {
  return formatWholeNumber(units)
}

// Deaths averted keeps meaningful precision at small values: "0.12", "1.2", "14".
export function formatDeathsAverted(deaths) {
  const fractionDigits = deaths >= 10 ? 0 : deaths >= 1 ? 1 : 2
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits
  }).format(deaths)
}
